import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Briefcase, ArrowRight, Sparkles } from "lucide-react";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function PublicCareers() {
  const { employerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const { brand } = useEmployerBrand(employerId);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/careers/${employerId}`)
      .then(({ data }) => !cancelled && setData(data))
      .catch((e) =>
        !cancelled && setErr(e?.response?.data?.detail || "Could not load roles")
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [employerId]);

  if (loading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </main>
    );
  }
  if (err) {
    return (
      <main className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="font-heading text-2xl font-semibold">Roles unavailable</h1>
        <p className="mt-2 text-sm text-black">{err}</p>
      </main>
    );
  }
  if (!data) return null;

  const { employer, jobs } = data;
  const heading = employer.company
    ? `Open roles at ${employer.company}`
    : `${employer.name || "Open"} roles`;

  return (
    <main
      data-testid="public-careers-page"
      className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-12"
    >
      <header className="flex flex-col items-start">
        <div className="flex items-center gap-3">
          {brand?.logo_url ? (
            <BrandLogo
              brand={brand}
              className="h-12 w-auto max-w-[160px] object-contain"
              alt={brand.company || "Company logo"}
            />
          ) : (
            !brand?.hide_powered_by && (
              <img
                src={H_LOGO_URL}
                alt="Hireloop PRO"
                className="h-10 w-10 object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            )
          )}
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
            Careers{brand?.company ? ` · ${brand.company}` : brand?.hide_powered_by ? "" : " · Hireloop PRO"}
          </div>
        </div>
        <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          {heading}
        </h1>
        <p className="mt-3 max-w-xl text-sm text-black">
          Apply by uploading your résumé below. We use AI scoring to surface the
          most promising candidates fast — usually within a day.
        </p>
        <Link
          to={`/c/${employerId}/status`}
          data-testid="public-careers-status-link"
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 hover:underline"
        >
          Already applied? Check your status →
        </Link>
      </header>

      <section className="mt-10 border border-slate-200">
        {jobs.length === 0 ? (
          <div
            data-testid="public-careers-empty"
            className="px-6 py-12 text-center text-sm text-slate-500"
          >
            No open roles right now. Check back soon.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {jobs.map((j) => (
              <li
                key={j.id}
                data-testid={`public-careers-job-${j.id}`}
                className="flex flex-col gap-3 p-5 hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 border border-slate-200 bg-slate-50 p-2">
                    <Briefcase className="h-4 w-4 text-blue-700" />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-medium text-black">
                      {j.title}
                    </div>
                    {j.description && (
                      <p className="mt-1 text-sm text-slate-600">
                        {j.description.slice(0, 220)}
                        {j.description.length > 220 ? "…" : ""}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  data-testid={`public-careers-apply-${j.id}`}
                  onClick={() =>
                    navigate(`/c/${employerId}/j/${j.id}`)
                  }
                  className="rounded-sm bg-blue-700 text-white hover:bg-blue-600"
                  size="sm"
                >
                  Apply
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!brand?.hide_powered_by && (
        <p className="mt-10 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-slate-400">
          <Sparkles className="h-3 w-3" /> Powered by Hireloop PRO
        </p>
      )}
    </main>
  );
}
